import React from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogClose } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useImageUrl } from '@/hooks/useImageUrl';

interface SelfieImageZoomProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  username: string;
  aiAnalysis?: {
    confidence?: number;
    qualityScore?: number;
    approved?: boolean;
    reason?: string;
    visionApiData?: {
      detectionConfidence?: number;
      blurred?: string;
      underExposed?: string;
      headwear?: string;
      landmarks?: {
        total: number;
        leftEye: boolean;
        rightEye: boolean;
        nose: boolean;
        mouth: boolean;
      };
      faceAngles?: {
        roll?: number;
        pan?: number;
        tilt?: number;
      };
      emotions?: {
        joy?: string;
        sorrow?: string;
        anger?: string;
        surprise?: string;
      };
      analysisTimestamp?: string;
    };
  };
}

const SelfieImageZoom: React.FC<SelfieImageZoomProps> = ({
  isOpen,
  onClose,
  imageUrl,
  username,
  aiAnalysis
}) => {
  const { url: signedImageUrl, isLoading } = useImageUrl(imageUrl, 'zoom');
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0 bg-black/95 border-white/10">
        <DialogDescription className="sr-only">
          Visualize a selfie de verificação de {username} com análise de IA
        </DialogDescription>
        <div className="relative flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div>
              <h3 className="text-white font-semibold">Selfie de Verificação</h3>
              <p className="text-white/60 text-sm">@{username}</p>
            </div>
            <DialogClose className="text-white hover:text-white/70 transition-colors">
              <X className="h-5 w-5" />
            </DialogClose>
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center p-4 overflow-auto">
            {isLoading ? (
              <div className="animate-pulse bg-white/10 rounded w-full max-w-md h-[60vh]" />
            ) : (
              <img
                src={signedImageUrl || imageUrl}
                alt={`Selfie de ${username}`}
                className="max-w-full max-h-[60vh] object-contain rounded"
              />
            )}
          </div>

          {/* AI Analysis */}
          {aiAnalysis && (
            <div className="p-4 border-t border-white/10 bg-white/5 max-h-[30vh] overflow-y-auto">
              <h4 className="text-white font-medium mb-3 text-sm">Análise de IA</h4>
              
              {/* Resumo Principal */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {aiAnalysis.approved !== undefined && (
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-white/60 text-xs">Status</p>
                    <Badge className={aiAnalysis.approved ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}>
                      {aiAnalysis.approved ? 'Aprovado' : 'Rejeitado'}
                    </Badge>
                  </div>
                )}
                {aiAnalysis.confidence !== undefined && (
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-white/60 text-xs">Confiança</p>
                    <p className="text-white font-medium">
                      {Math.round(aiAnalysis.confidence * 100)}%
                    </p>
                  </div>
                )}
                {aiAnalysis.qualityScore !== undefined && (
                  <div className="bg-white/5 rounded p-2">
                    <p className="text-white/60 text-xs">Qualidade</p>
                    <p className="text-white font-medium">
                      {aiAnalysis.qualityScore}/10
                    </p>
                  </div>
                )}
              </div>

              {/* Motivo da rejeição */}
              {aiAnalysis.reason && (
                <div className="bg-red-500/10 border border-red-500/20 rounded p-3 mb-4">
                  <p className="text-red-400 text-xs font-medium mb-1">Motivo da Rejeição:</p>
                  <p className="text-white/80 text-sm">{aiAnalysis.reason}</p>
                </div>
              )}
              
              {/* Detalhes Completos da Vision API */}
              {aiAnalysis.visionApiData && (
                <div className="space-y-3">
                  <h5 className="text-white/80 font-medium text-xs border-t border-white/10 pt-3">
                    Detalhes da Google Vision API
                  </h5>
                  
                  {/* Detecções */}
                  <div className="grid grid-cols-2 gap-2">
                    {aiAnalysis.visionApiData.blurred && (
                      <div className="bg-white/5 rounded p-2">
                        <p className="text-white/60 text-xs">Desfoque</p>
                        <Badge className={
                          aiAnalysis.visionApiData.blurred === 'VERY_UNLIKELY' || aiAnalysis.visionApiData.blurred === 'UNLIKELY' ? 'bg-green-500/20 text-green-300' :
                          aiAnalysis.visionApiData.blurred === 'POSSIBLE' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }>
                          {aiAnalysis.visionApiData.blurred}
                        </Badge>
                      </div>
                    )}
                    {aiAnalysis.visionApiData.underExposed && (
                      <div className="bg-white/5 rounded p-2">
                        <p className="text-white/60 text-xs">Subexposta</p>
                        <Badge className={
                          aiAnalysis.visionApiData.underExposed === 'VERY_UNLIKELY' || aiAnalysis.visionApiData.underExposed === 'UNLIKELY' ? 'bg-green-500/20 text-green-300' :
                          aiAnalysis.visionApiData.underExposed === 'POSSIBLE' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }>
                          {aiAnalysis.visionApiData.underExposed}
                        </Badge>
                      </div>
                    )}
                    {aiAnalysis.visionApiData.headwear && (
                      <div className="bg-white/5 rounded p-2">
                        <p className="text-white/60 text-xs">Acessórios (Boné/Chapéu)</p>
                        <Badge className={
                          aiAnalysis.visionApiData.headwear === 'VERY_UNLIKELY' || aiAnalysis.visionApiData.headwear === 'UNLIKELY' ? 'bg-green-500/20 text-green-300' :
                          aiAnalysis.visionApiData.headwear === 'POSSIBLE' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }>
                          {aiAnalysis.visionApiData.headwear}
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Landmarks */}
                  {aiAnalysis.visionApiData.landmarks && (
                    <div className="bg-white/5 rounded p-3">
                      <p className="text-white/60 text-xs mb-2">
                        Características Detectadas ({aiAnalysis.visionApiData.landmarks.total} landmarks)
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${aiAnalysis.visionApiData.landmarks.leftEye ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-white/80">Olho Esquerdo</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${aiAnalysis.visionApiData.landmarks.rightEye ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-white/80">Olho Direito</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${aiAnalysis.visionApiData.landmarks.nose ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-white/80">Nariz</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${aiAnalysis.visionApiData.landmarks.mouth ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-white/80">Boca</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Ângulos da Face */}
                  {aiAnalysis.visionApiData.faceAngles && (
                    <div className="bg-white/5 rounded p-3">
                      <p className="text-white/60 text-xs mb-2">Ângulos da Face</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        {aiAnalysis.visionApiData.faceAngles.roll !== undefined && (
                          <div>
                            <span className="text-white/60">Roll:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.faceAngles.roll.toFixed(1)}°</span>
                          </div>
                        )}
                        {aiAnalysis.visionApiData.faceAngles.pan !== undefined && (
                          <div>
                            <span className="text-white/60">Pan:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.faceAngles.pan.toFixed(1)}°</span>
                          </div>
                        )}
                        {aiAnalysis.visionApiData.faceAngles.tilt !== undefined && (
                          <div>
                            <span className="text-white/60">Tilt:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.faceAngles.tilt.toFixed(1)}°</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Emoções */}
                  {aiAnalysis.visionApiData.emotions && (
                    <div className="bg-white/5 rounded p-3">
                      <p className="text-white/60 text-xs mb-2">Emoções Detectadas</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        {aiAnalysis.visionApiData.emotions.joy && (
                          <div>
                            <span className="text-white/60">Alegria:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.emotions.joy}</span>
                          </div>
                        )}
                        {aiAnalysis.visionApiData.emotions.sorrow && (
                          <div>
                            <span className="text-white/60">Tristeza:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.emotions.sorrow}</span>
                          </div>
                        )}
                        {aiAnalysis.visionApiData.emotions.anger && (
                          <div>
                            <span className="text-white/60">Raiva:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.emotions.anger}</span>
                          </div>
                        )}
                        {aiAnalysis.visionApiData.emotions.surprise && (
                          <div>
                            <span className="text-white/60">Surpresa:</span>
                            <span className="text-white ml-1">{aiAnalysis.visionApiData.emotions.surprise}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {aiAnalysis.visionApiData?.analysisTimestamp && (
                <p className="text-white/40 text-xs mt-3 pt-3 border-t border-white/10">
                  Analisado em {new Date(aiAnalysis.visionApiData.analysisTimestamp).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SelfieImageZoom;
